import axios from "axios";

// Configuration de base d'axios
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Récupération du token depuis sessionStorage
const getToken = () => {
  const authUser = sessionStorage.getItem("authUser");
  return authUser ? JSON.parse(authUser).data?.token : null;
};

// Configuration initiale du token d'autorisation
const token = getToken();
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Liste des routes à exclure des headers d'autorisation
const excludedRoutes = [
  '/login',
  '/register',
  '/public',
  '/diet-food-recette',
 '/usersApi/api/alimentary/mealPlan',
  // Ajoutez ici d'autres routes à exclure
];

// Fonction pour vérifier si une URL doit être exclue
const isExcludedRoute = (url) => {
  if (!url) return false;
  return excludedRoutes.some(prefix => url.startsWith(prefix));
};

// Intercepteur de requêtes (request)
axios.interceptors.request.use(
  (config) => {
    // Ne pas ajouter le token d'autorisation pour les routes exclues
    if (!isExcludedRoute(config.url)) {
      // Vérifier et mettre à jour le token à chaque requête
      const currentToken = getToken();
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
    } else {
      // Supprimer le header d'autorisation pour les routes exclues
      delete config.headers.Authorization;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponses (response)
axios.interceptors.response.use(
  (response) => {
    // Retourner directement les données si disponibles
    return response.data ? response.data : response;
  },
  (error) => {
    // Gestion des erreurs HTTP
    let message;
    
    // Vérifier si l'erreur a une réponse
    const status = error.response ? error.response.status : null;
    
    switch (status) {
      case 400:
        message = "Requête incorrecte";
        break;
      case 401:
        message = "Non autorisé - Identifiants invalides";
        // Possibilité de rediriger vers la page de connexion
        // window.location.href = "/login";
        break;
      case 403:
        message = "Accès refusé";
        break;
      case 404:
        message = "Ressource non trouvée";
        break;
      case 500:
        message = "Erreur interne du serveur";
        break;
      default:
        message = error.message || "Une erreur est survenue";
    }
    
    // Vous pouvez également gérer les notifications d'erreur ici
    // Par exemple: toast.error(message)
    
    return Promise.reject({ message, originalError: error });
  }
);

/**
 * Met à jour l'en-tête d'autorisation
 * @param {string} token - Le token JWT
 */
const setAuthorization = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

/**
 * Récupère l'utilisateur connecté depuis sessionStorage
 * @returns {Object|null} - L'utilisateur connecté ou null
 */
const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

/**
 * Classe pour gérer les appels API
 */
class APIClient {
  /**
   * Récupère des données depuis l'URL donnée
   * @param {string} url - L'URL de l'endpoint
   * @param {Object} params - Les paramètres de requête (optionnels)
   * @returns {Promise} - La promesse de la requête
   */
  get = (url, params) => {
    if (params) {
      const queryString = Object.keys(params)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join('&');
      return axios.get(`${url}?${queryString}`);
    }
    return axios.get(url);
  };

  /**
   * Envoie des données à l'URL donnée (POST)
   * @param {string} url - L'URL de l'endpoint
   * @param {Object} data - Les données à envoyer
   * @param {Object} config - Configuration supplémentaire (optionnelle)
   * @returns {Promise} - La promesse de la requête
   */
  create = (url, data, config = {}) => {
    return axios.post(url, data, config);
  };

  /**
   * Alias pour la méthode create (POST)
   */
  post = (url, data, config = {}) => {
    return this.create(url, data, config);
  };

  /**
   * Met à jour partiellement des données (PATCH)
   * @param {string} url - L'URL de l'endpoint
   * @param {Object} data - Les données à mettre à jour
   * @param {Object} config - Configuration supplémentaire (optionnelle)
   * @returns {Promise} - La promesse de la requête
   */
  update = (url, data, config = {}) => {
    return axios.patch(url, data, config);
  };

  /**
   * Remplace complètement des données (PUT)
   * @param {string} url - L'URL de l'endpoint
   * @param {Object} data - Les données de remplacement
   * @param {Object} config - Configuration supplémentaire (optionnelle)
   * @returns {Promise} - La promesse de la requête
   */
  put = (url, data, config = {}) => {
    return axios.put(url, data, config);
  };

  /**
   * Supprime une ressource
   * @param {string} url - L'URL de l'endpoint
   * @param {Object} config - Configuration supplémentaire (optionnelle)
   * @returns {Promise} - La promesse de la requête
   */
  delete = (url, config = {}) => {
    return axios.delete(url, config);
  };
}

export { APIClient, setAuthorization, getLoggedinUser };